<script setup lang="ts">
import { ref, computed } from 'vue'

import { type PreviewIncidentReportResponse } from '@roadfootage/api-client'
import { formatIncidentType, formatIncidentDateTime } from '../utils/format'

const props = defineProps<{
  incident: PreviewIncidentReportResponse
}>()

// --- Hover & Video Logic ---
const HOVER_DELAY_MS = 600
let hoverTimeout: ReturnType<typeof setTimeout> | null = null
const intentToPlay = ref(false)
const isVideoReady = ref(false)

const onMouseEnter = () => {
  if (!props.incident.previewUrl) return

  hoverTimeout = setTimeout(() => {
    intentToPlay.value = true
  }, HOVER_DELAY_MS)
}

const onVideoPlaying = () => {
  // Fired by the native HTML5 video element when it actually starts advancing frames
  isVideoReady.value = true
}

const onMouseLeave = () => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = null
  }
  intentToPlay.value = false
  isVideoReady.value = false
}

// --- Data Formatting ---
const formattedDate = computed(() => formatIncidentDateTime(props.incident.incidentDateTime, null))

// --- Fallback Styling ---
// Generates a consistent, unique CSS gradient based on the incident ID.
// This prevents JARring random colour changes on re-renders, while satisfying the requirement.
const fallbackStyle = computed(() => {
  const idStr = props.incident.id || 'default'
  // Simple deterministic hash
  let hash = 0
  for (let i = 0; i < idStr.length; i++) {
    hash = idStr.charCodeAt(i) + ((hash << 5) - hash)
  }

  const hue1 = Math.abs(hash % 360)
  const hue2 = Math.abs((hash * 2) % 360)

  return {
    background: `linear-gradient(135deg, hsl(${hue1}, 40%, 60%), hsl(${hue2}, 60%, 30%))`,
    backdropFilter: 'blur(10px)'
  }
})
</script>

<template>
  <q-card
    class="incident-card cursor-pointer q-hoverable"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="media-wrapper bg-grey-3 overflow-hidden relative-position full-width">
      <div class="absolute-full crossfade-layer" :class="{ 'opacity-0': isVideoReady }">
        <q-img
          v-if="!incident.hasContentWarning && incident.thumbnailUrl"
          :src="incident.thumbnailUrl"
          class="fit"
        >
          <template #error>
            <div class="fit flex flex-center bg-grey-3">
              <q-icon name="mdi-image-broken" size="md" color="grey-6" />
            </div>
          </template>
        </q-img>

        <div v-else class="fit flex flex-center text-center" :style="fallbackStyle">
          <q-icon name="mdi-camera-off" size="xl" color="white" class="opacity-50" />
        </div>
      </div>

      <video
        v-if="incident.previewUrl && intentToPlay"
        :src="incident.previewUrl"
        class="absolute-full fit object-cover crossfade-layer"
        :class="{ 'opacity-0': !isVideoReady }"
        @playing="onVideoPlaying"
        autoplay
        muted
        loop
        playsinline
        preload="metadata"
      ></video>

      <div v-if="incident.hasContentWarning" class="absolute-top-right q-pa-sm">
        <q-badge color="negative" text-color="white" label="Content Warning" />
      </div>
    </div>

    <q-card-section>
      <div>
        <div class="text-overline text-primary">
          {{ formatIncidentType(incident.incidentReportType) }}
        </div>
        <div
          class="text-subtitle2 description-block q-mb-sm"
          :title="incident.incidentDescription || ''"
        >
          {{ incident.incidentDescription || 'No description provided.' }}
        </div>
      </div>

      <div class="text-caption text-grey-7 row items-start no-wrap address-row">
        <q-icon name="mdi-map-marker" class="q-mr-xs q-mt-xs" />
        <span class="address-clamp" :title="incident.address?.formattedAddress ?? ''">{{
          incident.address?.formattedAddress || 'Location unknown'
        }}</span>
      </div>
      <div class="text-caption text-grey-7 row items-center no-wrap q-mt-xs">
        <q-icon name="mdi-calendar" class="q-mr-xs" />
        <span>{{ formattedDate }}</span>
      </div>
    </q-card-section>
  </q-card>
</template>

<style lang="scss" scoped>
.incident-card {
  transition:
    transform 0.2s cubic-bezier(0.25, 0.8, 0.5, 1),
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.12),
      0 4px 8px rgba(0, 0, 0, 0.06);
  }
}

.media-wrapper {
  /* Modern native aspect ratio handling */
  aspect-ratio: 16 / 9;
}

.crossfade-layer {
  transition: opacity 1s ease-in-out;
  /* Ensure hardware acceleration during crossfade */
  will-change: opacity;
}

.object-cover {
  object-fit: cover;
}

.opacity-0 {
  opacity: 0;
  /* Prevent interaction with the hidden layer */
  pointer-events: none;
}

.opacity-50 {
  opacity: 0.5;
}

.description-block {
  line-height: 1.5;
  height: 3lh;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

.address-row {
  line-height: 1.5;
  height: 2lh;
  overflow: hidden;
}

.address-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.z-top {
  z-index: 10;
}
</style>
